/**
 * Friends — the real thing.
 *
 * Data model (Firestore, top-level collections):
 *   - `friendships/{compositeId}` where compositeId = sortedUidA__sortedUidB
 *     so the same pair always resolves to the same doc regardless of who
 *     initiated. Avoids duplicate friendships.
 *     Fields: { uids: [a, b], requestedBy, status: 'pending'|'accepted',
 *              createdAt, acceptedAt?, profiles: { [uid]: {displayName, avatar} } }
 *   - `userIndex/{displayName_lowercase}` → { uid, displayName, avatar }
 *     Lets us search by name without scanning the entire users collection.
 *
 * EVERY MUTATION IN HERE THROWS ON FAILURE.
 *
 * That is the point of this file. The bug report that produced it — "clicking
 * add friend doesnt really do anything really" — was possible because every
 * write path ended in `catch { console.warn }`: a rejected write and a
 * successful one were pixel-for-pixel identical to the person clicking. A
 * social feature whose failures are invisible is one nobody can trust. Failures
 * now travel to the caller as a `FriendsError` carrying a sentence a human can
 * read, and the screen is responsible for showing it. Reads may still degrade
 * quietly — a friends list that fails to load is visibly empty — but a write
 * never may.
 */

import { db } from './firebase';
import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, query, where,
  serverTimestamp, deleteDoc, orderBy, limit as fsLimit, startAt, endAt,
  onSnapshot, type Unsubscribe, type Query, type QuerySnapshot,
} from 'firebase/firestore';
import { websocketService } from './websocketService';

export type FriendshipStatus = 'pending' | 'accepted';

export interface FriendProfile {
  uid: string;
  displayName: string;
  avatar: string;
}

export interface Friendship {
  id: string;
  uids: [string, string];
  /** The uid that sent the request. Only the OTHER party may accept. */
  requestedBy: string;
  status: FriendshipStatus;
  createdAt?: any;
  acceptedAt?: any;
  /** Resolved profile of the *other* party from `me`'s perspective. */
  other: FriendProfile;
}

export interface UserIndexRow {
  uid: string;
  displayName: string;
  avatar: string;
}

/** What actually happened when you pressed Add. The screen says this out loud. */
export type SendOutcome =
  | 'sent'              // new pending request created
  | 'already-sent'      // you'd already asked; nothing changed
  | 'accepted-incoming' // they had already asked YOU, so pressing Add accepts
  | 'already-friends';  // you're already friends; nothing changed

export class FriendsError extends Error {
  /** A sentence safe to put in a toast. */
  readonly userMessage: string;
  readonly cause?: unknown;
  constructor(userMessage: string, cause?: unknown) {
    super(userMessage);
    this.name = 'FriendsError';
    this.userMessage = userMessage;
    this.cause = cause;
  }
}

/** Atom, the AI. Everyone can play it; nobody is "friends" with it. */
export const ATOM_UID = 'atom_demo_ai';

/**
 * Turn a raw Firestore/network failure into something a person can act on.
 * `permission-denied` is the one that matters: it is what a stale security rule
 * looks like from the client, and it is exactly the class of failure that spent
 * this feature's whole life hidden inside a console.warn.
 */
function asFriendsError(e: any, fallback: string): FriendsError {
  const code = e?.code || '';
  if (code === 'permission-denied') {
    return new FriendsError("You don't have permission to do that. Try signing in again.", e);
  }
  if (code === 'unavailable' || code === 'failed-precondition') {
    return new FriendsError("You're offline. Check your connection and try again.", e);
  }
  if (code === 'not-found') {
    return new FriendsError('That request no longer exists.', e);
  }
  return new FriendsError(fallback, e);
}

/** Stable composite id for a pair. Lexicographic sort so (a,b) and (b,a) collide. */
export function pairId(a: string, b: string): string {
  return [a, b].sort().join('__');
}

/**
 * Firestore has no `startsWith`. The idiom is an ordered range from the prefix
 * to the prefix plus the highest code point that can appear in a doc id, and
 * U+F8FF is that sentinel.
 *
 * It used to read `endAt(q + '')` — the sentinel had been flattened to an empty
 * string somewhere in this file's history, which collapses the range to [q, q],
 * i.e. an exact doc-id match. Typing "ma" could never find "maya"; search only
 * ever worked if you typed somebody's whole name, correctly, first try.
 */
const PREFIX_SENTINEL = '\uf8ff';

/**
 * List public players the searcher could befriend. Drawn from the
 * `users` collection so we can filter by privacy settings.
 *
 * Filters applied:
 *   - Excludes the searcher themselves
 *   - Excludes anyone who set privacy.allowFriendRequests = false
 *   - Excludes accountType:'ai' (Atom is not a person)
 *   - Excludes profiles missing displayName (avoids "Player" placeholders)
 *   - When `excludeDemo` is true (Demo Mode off), excludes isDemo:true
 */
export async function listSuggestedPlayers(
  searcherUid: string,
  limit = 20,
  excludeDemo = false,
): Promise<UserIndexRow[]> {
  if (!db) return [];
  try {
    const ref = collection(db, 'users');
    const snap = await getDocs(query(ref, fsLimit(120)));
    const out: UserIndexRow[] = [];
    snap.forEach(d => {
      const data = d.data() as any;
      const uid = data.uid || d.id;
      if (uid === searcherUid) return;
      if (uid === ATOM_UID) return;
      if (data.privacy?.allowFriendRequests === false) return;
      if (data.accountType === 'ai') return;
      if (excludeDemo && data.isDemo === true) return;
      // Skip anonymous placeholder profiles ("Anonymous Player" / "Player")
      // so the suggestion list shows real, identifiable users.
      const name = (data.displayName || '').trim();
      if (!name || name === 'Anonymous Player' || name === 'Player') return;
      out.push({ uid, displayName: name, avatar: data.avatar || 'fox' });
    });
    return out.slice(0, limit);
  } catch (e) {
    console.warn('[friendsService] listSuggestedPlayers failed:', (e as any)?.message || e);
    return [];
  }
}

/**
 * Search by display-name prefix. Case-insensitive. Returns up to 10 hits.
 * Excludes the searcher's own uid. Empty query returns [].
 */
export async function searchUsers(searcherUid: string, q: string): Promise<UserIndexRow[]> {
  const needle = q.trim().toLowerCase();
  if (!db || !needle) return [];
  try {
    const ref = collection(db, 'userIndex');
    const qq = query(
      ref,
      orderBy('__name__'),
      startAt(needle),
      endAt(needle + PREFIX_SENTINEL),
      fsLimit(10),
    );
    const snap = await getDocs(qq);
    const out: UserIndexRow[] = [];
    snap.forEach(d => {
      const data = d.data() as any;
      if (data?.uid && data.uid !== searcherUid && data.uid !== ATOM_UID) {
        out.push({ uid: data.uid, displayName: data.displayName || d.id, avatar: data.avatar || 'fox' });
      }
    });
    return out;
  } catch (e) {
    console.warn('[friendsService] searchUsers failed:', (e as any)?.message || e);
    return [];
  }
}

/** Upsert the searcher's own user-index row. Call after profile updates so
 *  search results stay current. Idempotent. Best-effort: a missing index row
 *  makes you unfindable, it does not break the page you're standing on. */
export async function upsertOwnIndexRow(uid: string, displayName: string, avatar: string): Promise<void> {
  if (!db || !uid) return;
  const key = (displayName || '').trim().toLowerCase();
  if (!key) return;
  try {
    await setDoc(doc(db, 'userIndex', key), {
      uid, displayName, avatar: safeAvatar(avatar), updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (e) {
    console.warn('[friendsService] upsertOwnIndexRow failed:', (e as any)?.message || e);
  }
}

/**
 * An avatar field must be an avatar ID ('fox', 'owl', …) — never a photo URL,
 * never undefined. ProfilePreview used to pass `currentUser.photoURL` straight
 * into this slot, and Firestore rejects `undefined` outright with an
 * invalid-data throw. The caller then swallowed it. That is one of the ways
 * "Add" could genuinely do nothing at all.
 */
function safeAvatar(a: unknown): string {
  return typeof a === 'string' && a && !a.startsWith('http') ? a : 'fox';
}
function safeName(n: unknown): string {
  const s = typeof n === 'string' ? n.trim() : '';
  return s || 'Player';
}

/**
 * Send a friend request — or do the right thing if one already exists.
 *
 * This returns an outcome instead of void because pressing Add twice, and
 * pressing Add on someone who has already asked YOU, are both things real
 * people do, and neither should error or produce a duplicate. The pair doc is
 * keyed on the sorted uid pair, so the state machine is closed:
 *
 *   (no doc)        + Add  ->  pending, requestedBy = me   'sent'
 *   pending by me   + Add  ->  no-op                       'already-sent'
 *   pending by them + Add  ->  accepted                    'accepted-incoming'
 *   accepted        + Add  ->  no-op                       'already-friends'
 */
export async function sendFriendRequest(
  fromUid: string,
  toUid: string,
  fromName: string,
  fromAvatar: string,
  toName: string,
  toAvatar: string,
): Promise<SendOutcome> {
  if (!db) throw new FriendsError('Friends are unavailable right now.');
  if (!fromUid) throw new FriendsError('Sign in to add friends.');
  if (!toUid) throw new FriendsError("We couldn't find that player.");
  if (fromUid === toUid) throw new FriendsError("You can't add yourself.");
  if (toUid === ATOM_UID) throw new FriendsError('Atom is the AI — you can play it any time, no invite needed.');

  const ref = doc(db, 'friendships', pairId(fromUid, toUid));

  let existing: any = null;
  try {
    const snap = await getDoc(ref);
    existing = snap.exists() ? snap.data() : null;
  } catch (e) {
    throw asFriendsError(e, "We couldn't check that friendship. Try again.");
  }

  if (existing?.status === 'accepted') return 'already-friends';
  if (existing?.status === 'pending') {
    if (existing.requestedBy === fromUid) return 'already-sent';
    // They asked first. Pressing Add is an accept.
    try {
      await updateDoc(ref, { status: 'accepted', acceptedAt: serverTimestamp() });
      return 'accepted-incoming';
    } catch (e) {
      throw asFriendsError(e, "We couldn't accept that request. Try again.");
    }
  }

  try {
    await setDoc(ref, {
      uids: [fromUid, toUid].sort(),
      requestedBy: fromUid,
      status: 'pending',
      createdAt: serverTimestamp(),
      // Denormalized so a friends list is ONE read, not one read per friend.
      profiles: {
        [fromUid]: { displayName: safeName(fromName), avatar: safeAvatar(fromAvatar) },
        [toUid]: { displayName: safeName(toName), avatar: safeAvatar(toAvatar) },
      },
    });
    return 'sent';
  } catch (e) {
    throw asFriendsError(e, "We couldn't send that request. Try again.");
  }
}

/**
 * Accept an incoming request. Only the party who did NOT send it may accept —
 * checked here so the message is clear, and enforced again in firestore.rules,
 * because a client-side check is a courtesy, not a control.
 */
export async function acceptFriendRequest(meUid: string, otherUid: string): Promise<void> {
  if (!db) throw new FriendsError('Friends are unavailable right now.');
  if (!meUid || !otherUid) throw new FriendsError('Sign in to accept requests.');

  const ref = doc(db, 'friendships', pairId(meUid, otherUid));
  let data: any;
  try {
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new FriendsError('That request no longer exists.');
    data = snap.data();
  } catch (e) {
    if (e instanceof FriendsError) throw e;
    throw asFriendsError(e, "We couldn't accept that request. Try again.");
  }

  if (data.status === 'accepted') return; // idempotent — already friends
  if (data.requestedBy === meUid) {
    throw new FriendsError('You sent this request — they have to accept it.');
  }

  try {
    await updateDoc(ref, { status: 'accepted', acceptedAt: serverTimestamp() });
  } catch (e) {
    throw asFriendsError(e, "We couldn't accept that request. Try again.");
  }
}

/**
 * Decline an incoming request, cancel one you sent, or unfriend someone. All
 * three are the same write — the pair doc goes away — and which one it reads as
 * depends only on the doc's status, which the caller already knows.
 */
export async function declineOrRemoveFriend(meUid: string, otherUid: string): Promise<void> {
  if (!db) throw new FriendsError('Friends are unavailable right now.');
  if (!meUid || !otherUid) throw new FriendsError('Sign in first.');
  if (otherUid === ATOM_UID) throw new FriendsError('Atom is the AI, not a friend you can remove.');
  try {
    await deleteDoc(doc(db, 'friendships', pairId(meUid, otherUid)));
  } catch (e) {
    throw asFriendsError(e, "We couldn't do that. Try again.");
  }
}

/** Shared mapper for the one-shot read and the live subscription, so the two
 *  can never drift into disagreeing about what a friendship is. */
function toFriendship(id: string, data: any, meUid: string): Friendship | null {
  const uids: string[] = Array.isArray(data?.uids) ? data.uids : [];
  const otherUid = uids.find(x => x !== meUid) || '';
  // A doc that does not actually name me is not mine to render. This can only
  // happen if a rule regresses — render nothing rather than something wrong.
  if (!otherUid || !uids.includes(meUid)) return null;
  const p = data.profiles?.[otherUid] || {};
  return {
    id,
    uids: data.uids,
    requestedBy: data.requestedBy,
    status: data.status,
    createdAt: data.createdAt,
    acceptedAt: data.acceptedAt,
    other: { uid: otherUid, displayName: safeName(p.displayName), avatar: safeAvatar(p.avatar) },
  };
}

/**
 * List all friendships for the user, pending ones included in both directions
 * so the UI can render Accept buttons and a "Sent" list.
 *
 * Atom is NOT in here, deliberately. It used to be injected as a synthetic
 * accepted friendship into every user's list, so a user with zero friends saw
 * "Friends (1)" and a row that looked exactly like a person — with a green
 * online dot that was hard-coded on. Atom is the AI. It gets its own affordance
 * ("Play Atom"), not a seat in your friends list.
 */
export async function fetchMyFriends(uid: string): Promise<Friendship[]> {
  if (!db || !uid) return [];
  try {
    const snap = await getDocs(query(collection(db, 'friendships'), where('uids', 'array-contains', uid)));
    const out: Friendship[] = [];
    snap.forEach(d => {
      const f = toFriendship(d.id, d.data(), uid);
      if (f) out.push(f);
    });
    return out;
  } catch (e) {
    console.warn('[friendsService] fetchMyFriends failed:', (e as any)?.message || e);
    return [];
  }
}

/**
 * Live subscribe to the user's friendships. `onChange` fires whenever Firestore
 * pushes — which is what makes an accept appear on the sender's screen without
 * a refresh. `onError` fires if the listener is rejected outright, and the
 * screen shows it, because a friends list that is empty because it was DENIED
 * looks exactly like a friends list that is empty because you have no friends.
 */
export function subscribeMyFriends(
  uid: string,
  onChange: (rows: Friendship[]) => void,
  onError?: (e: FriendsError) => void,
): Unsubscribe {
  if (!db || !uid) return () => {};
  try {
    const qq = query(collection(db, 'friendships'), where('uids', 'array-contains', uid));
    return onSnapshot(qq, snap => {
      const out: Friendship[] = [];
      snap.forEach(d => {
        const f = toFriendship(d.id, d.data(), uid);
        if (f) out.push(f);
      });
      onChange(out);
    }, err => {
      console.warn('[friendsService] subscribeMyFriends snapshot error:', err?.message || err);
      onError?.(asFriendsError(err, "We couldn't load your friends."));
    });
  } catch (e) {
    onError?.(asFriendsError(e, "We couldn't load your friends."));
    return () => {};
  }
}

/**
 * The friendships query, and its mapper, for the shared live hook.
 *
 * `subscribeMyFriends` above is the imperative form and FriendsScreen uses it.
 * These exist so that the surfaces which only ever asked ONE question of the
 * friendship list — *"what is my status with this one person?"* — can subscribe
 * with the same hook every other live surface uses.
 *
 * That question was being answered by a one-shot read, which is why the Add
 * button lied: the other person accepts, and the button you are looking at goes
 * on saying "Requested" until you reload the page. On a preview sheet you opened
 * from a game roster, that is the whole feature failing quietly.
 */
export function myFriendshipsQuery(uid: string): Query | null {
  if (!db || !uid) return null;
  return query(collection(db, 'friendships'), where('uids', 'array-contains', uid));
}

export const mapFriendships =
  (meUid: string) =>
    (snap: QuerySnapshot): Friendship[] => {
      const out: Friendship[] = [];
      snap.forEach(d => {
        const f = toFriendship(d.id, d.data(), meUid);
        if (f) out.push(f);
      });
      return out;
    };

/** My status with one person, from a live friendships list. */
export type FriendStatus = 'self' | 'friends' | 'pending-out' | 'pending-in' | 'none';

export function statusWith(rows: Friendship[], meUid: string, otherUid: string): FriendStatus {
  if (!meUid || !otherUid) return 'none';
  if (meUid === otherUid) return 'self';
  if (otherUid === ATOM_UID) return 'friends';  // Atom is playable by everyone
  const f = rows.find(x => x.other.uid === otherUid);
  if (!f) return 'none';
  if (f.status === 'accepted') return 'friends';
  return f.requestedBy === meUid ? 'pending-out' : 'pending-in';
}

/**
 * How many people are waiting on YOU. Drives the badge on the Friends tab.
 *
 * This is the piece that was missing, and it is why a sent request vanished
 * into a void: the only surface that has ever shown an incoming request is a
 * tab inside a screen that nothing pointed at. Nobody knew to look, so nobody
 * accepted, so the sender's list never changed — and from the sender's chair
 * the button really did do nothing.
 */
export function subscribeIncomingRequestCount(
  uid: string,
  onChange: (count: number) => void,
): Unsubscribe {
  if (!db || !uid) return () => {};
  try {
    const qq = query(collection(db, 'friendships'), where('uids', 'array-contains', uid));
    return onSnapshot(qq, snap => {
      let n = 0;
      snap.forEach(d => {
        const data = d.data() as any;
        if (data.status === 'pending' && data.requestedBy !== uid) n += 1;
      });
      onChange(n);
    }, () => onChange(0));
  } catch {
    return () => {};
  }
}

/**
 * Ask the server which of the given uids are connected right now. The server
 * answers from its live socket map, so `friendsPresence` is the set of uids
 * that genuinely have an open connection.
 *
 * There is no fallback that invents presence. If the socket is down we return
 * the empty set and every friend reads Offline — the honest answer is "we don't
 * know", and we are not going to guess green. Atom is never queried and never
 * in this set: it is not a person who is or isn't online.
 */
export async function fetchFriendsPresence(uids: string[]): Promise<Set<string>> {
  const wanted = uids.filter(u => u && u !== ATOM_UID);
  return new Promise(resolve => {
    const svc: any = websocketService;
    if (!svc?.socket?.emit || wanted.length === 0) {
      resolve(new Set());
      return;
    }
    let done = false;
    const handler = (data: any) => {
      const online: string[] = Array.isArray(data?.online) ? data.online : [];
      finish(new Set(online));
    };
    const finish = (set: Set<string>) => {
      if (done) return;
      done = true;
      svc.off('friendsPresence', handler);
      resolve(set);
    };
    svc.on('friendsPresence', handler);
    svc.socket.emit('getFriendsPresence', { uids: wanted });
    setTimeout(() => finish(new Set()), 3000);
  });
}
